import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Send, MessageCircle, Info } from "lucide-react";

const PLACEHOLDERS = ["{{name}}", "{{pass_type}}", "{{amount}}", "{{status}}", "{{mobile}}", "{{email}}", "{{company_name}}", "{{event_name}}"];

export default function WhatsAppConfig() {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    api.get("/whatsapp/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg) return <div className="mt-4 text-[#71717A]">Loading…</div>;

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...cfg };
      delete body.api_key_set;
      if (apiKey) body.api_key = apiKey; else delete body.api_key;
      const { data } = await api.put("/whatsapp/config", body);
      setCfg(data);
      setApiKey("");
      toast.success("WhatsApp configuration saved");
    } catch (e) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      // save first so the test uses latest values
      const body = { ...cfg };
      delete body.api_key_set;
      if (apiKey) body.api_key = apiKey; else delete body.api_key;
      await api.put("/whatsapp/config", body);
      const { data } = await api.post("/whatsapp/test", { to: testTo });
      const describe = (label, r) => {
        if (!r) return `${label}: no response`;
        if (r.skipped) return `${label}: skipped (${r.reason})`;
        if (r.ok) return `${label}: sent ✓`;
        return `${label}: failed (HTTP ${r.status_code}) ${String(r.response || "").slice(0, 120)}`;
      };
      const userLine = describe("User", data.user);
      const adminLine = describe("Admin", data.admin);
      if (data.ok) toast.success(`${userLine}\n${adminLine}`);
      else toast.error(`${userLine}\n${adminLine}`);
    } catch (e) {
      toast.error("Test request failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-[#121212] p-6 mt-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-2xl font-800 uppercase tracking-tight flex items-center gap-2">
          <MessageCircle className="h-6 w-6 text-[#25D366]" /> WhatsApp Configuration
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wide text-[#A1A1AA]">Notifications</span>
          <Switch data-testid="wa-enabled-toggle" checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", v)} />
          <span className={`text-xs font-semibold ${cfg.enabled ? "text-[#25D366]" : "text-[#71717A]"}`}>{cfg.enabled ? "ON" : "OFF"}</span>
        </div>
      </div>
      <p className="text-sm text-[#71717A] mb-6">Connect your WhatsApp template API. Notifications fire on registration & payment (Day/Meraki) and sponsor enquiries.</p>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="WhatsApp API Base URL" required>
          <Input data-testid="wa-base-url-input" value={cfg.base_url || ""} onChange={(e) => set("base_url", e.target.value)} placeholder="https://api.aoc-portal.com/v1/whatsapp" />
        </Field>
        <Field label={`API Key / Token${cfg.api_key_set ? " (saved)" : ""}`} required>
          <Input data-testid="wa-api-key-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={cfg.api_key_set ? "•••••••• (leave blank to keep)" : "Enter API key"} />
        </Field>
        <Field label="Sender Number (business)" required>
          <Input data-testid="wa-sender-input" value={cfg.sender_number || ""} onChange={(e) => set("sender_number", e.target.value)} placeholder="91XXXXXXXXXX" />
        </Field>
        <Field label="Admin Notification Number">
          <Input data-testid="wa-admin-number-input" value={cfg.admin_number || ""} onChange={(e) => set("admin_number", e.target.value)} placeholder="91XXXXXXXXXX" />
        </Field>
        <Field label="Campaign Name">
          <Input data-testid="wa-campaign-input" value={cfg.campaign_name || ""} onChange={(e) => set("campaign_name", e.target.value)} placeholder="meraki-moto-mania" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="User Template Name" required>
            <Input data-testid="wa-user-template-name-input" value={cfg.user_template_name || ""} onChange={(e) => set("user_template_name", e.target.value)} placeholder="approved_template" />
          </Field>
          <Field label="Admin Template Name" required>
            <Input data-testid="wa-admin-template-name-input" value={cfg.admin_template_name || ""} onChange={(e) => set("admin_template_name", e.target.value)} placeholder="approved_template" />
          </Field>
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-border bg-[#0A0A0A] p-3 flex items-start gap-2">
        <Info className="h-4 w-4 text-[#FF4500] mt-0.5 shrink-0" />
        <p className="text-xs text-[#A1A1AA]">
          Placeholders you can use in templates (each becomes an ordered variable/param sent to your approved template):
          <span className="text-[#E4E4E7]"> {PLACEHOLDERS.join("  ")}</span>
        </p>
      </div>

      <div className="mt-4 space-y-1.5">
        <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">Message Template – User Confirmation <span className="text-[#FF4500]">*</span></Label>
        <Textarea data-testid="wa-user-template-input" rows={3} value={cfg.user_template || ""} onChange={(e) => set("user_template", e.target.value)} />
      </div>
      <div className="mt-4 space-y-1.5">
        <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">Message Template – Admin Alert <span className="text-[#FF4500]">*</span></Label>
        <Textarea data-testid="wa-admin-template-input" rows={3} value={cfg.admin_template || ""} onChange={(e) => set("admin_template", e.target.value)} />
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1 min-w-[200px] space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">Test recipient number</Label>
          <Input data-testid="wa-test-number-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="91XXXXXXXXXX (defaults to admin number)" />
        </div>
        <Button data-testid="wa-test-button" onClick={testConnection} disabled={testing} variant="outline" className="rounded-full border-border h-11">
          {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Test Connection
        </Button>
        <Button data-testid="wa-save-button" onClick={save} disabled={saving} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722] h-11">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Configuration
        </Button>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">{label} {required && <span className="text-[#FF4500]">*</span>}</Label>
      {children}
    </div>
  );
}
